/**
 * The decisions a channel's draft leaves its owner, the merch CSR (merch spec
 * 12 §6, "Quantity options" and "Alternatives"), and changing who that owner
 * is:
 *
 * - Choose quantities: which of the team's and the price list's quantities
 *   the line offers. The smallest offered is the line's own (quotes lists
 *   options from the line's own up); a missing quantity can be added at a
 *   price the CSR types. The price list's quantities are Promo Office's
 *   breaks, not prices the team quoted.
 * - Possible alternative: a line repeating an article becomes a quantity
 *   option of the line it repeats, stays as an extra line, or goes. An
 *   alternative that differs in more than quantity goes on a second draft
 *   quote, which this only explains.
 * - Change owner: on every draft quote, not only a channel's. Another CSR in
 *   the merchandising department for a channel's draft; any active staff user
 *   who can manage quotes for a staff-made draft.
 * - Choose contact: one of the customer's people, on every draft quote. A
 *   channel's draft is not opened in a basket (it would re-price the team's
 *   lines), so staff choose the contact here, and the draft quote can be
 *   published and shared once it has one.
 * - Choose delivery address: another of the customer's delivery addresses
 *   for every item line, on every draft quote. The delivery price is not
 *   recalculated: the draft quote's price stands. A new address is added on
 *   the customer's page (its own command), not here.
 *
 * Each posts through the page (`onSave`), which answers with the refusal to
 * show: a field-tagged one beside its input, the rest in the banner.
 */
import { useId, useState } from "react";
import { Link } from "react-router";
import type {
  ChosenQuantity,
  PossibleAlternativeResolution,
  QuoteLineView,
  QuoteView,
} from "@shared-core/data/quote-types";
import { penceOfPounds } from "@shared-core/domain/delivery-selection";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import {
  chosenQuantitiesOf,
  describeOffer,
  needsTypedCost,
  quantityRowProblems,
  quantityRowSourceWords,
  quantityRowsOf,
  repeatedLineOf,
  unitPriceWords,
  type QuantityRow,
} from "@shared-core/domain/quote-draft-checks";
import {
  quoteLineNo,
  type QuoteStepFailure,
} from "@shared-core/domain/quote-presentation";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Button } from "../../primitives/button";
import { Checkbox } from "../../primitives/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import { RadioGroup, RadioGroupItem } from "../../primitives/radio-group";

const count = (n: number) => n.toLocaleString("en-GB");

/** Offer · quantity · price · buy price, then where it comes from: beside them from sm, under them below. */
const ROW_GRID =
  "grid grid-cols-[1.25rem_minmax(0,5rem)_minmax(0,7rem)_minmax(0,7rem)] gap-x-3 gap-y-1 sm:grid-cols-[2.5rem_5rem_7rem_7rem_minmax(0,1fr)]";

function FailureBanner({ failure }: { failure: QuoteStepFailure | null }) {
  if (!failure) return null;
  return (
    <Alert variant="destructive" role="alert">
      <AlertDescription>{failure.summary}</AlertDescription>
    </Alert>
  );
}

function sortedOffered(rows: QuantityRow[]): QuantityRow[] {
  return rows
    .filter((r) => r.isOffered)
    .sort(
      (a, b) =>
        Number(a.quantity.replace(/,/g, "")) -
        Number(b.quantity.replace(/,/g, "")),
    );
}

// ── Choose quantities ──────────────────────────────────────────────────────

export function ChooseQuantitiesDialog({
  quote,
  line,
  busy,
  onClose,
  onSave,
}: {
  quote: QuoteView;
  line: QuoteLineView;
  busy: boolean;
  onClose: () => void;
  onSave: (quantities: ChosenQuantity[]) => Promise<QuoteStepFailure | null>;
}) {
  const id = useId();
  const [rows, setRows] = useState<QuantityRow[]>(() => quantityRowsOf(line));
  const [added, setAdded] = useState(0);
  const [failure, setFailure] = useState<QuoteStepFailure | null>(null);
  const problems = quantityRowProblems(rows);
  const offered = sortedOffered(rows);
  const hasProblem = Object.keys(problems).length > 0 || offered.length === 0;
  const priceListLabel =
    line.quantity_candidates?.[0]?.source_label ??
    "From Promo Office's price list, not quoted by the team";
  // A refusal names a quantity by its index in the request, which is the offered rows smallest first.
  const serverProblem = (
    row: QuantityRow,
    input: "quantity" | "price_pence" | "cost_pence",
  ) => {
    const i = offered.findIndex((r) => r.key === row.key);
    return i < 0 ? undefined : failure?.fieldErrors[`quantities.${i}.${input}`];
  };
  const update = (key: string, patch: Partial<QuantityRow>) =>
    setRows((current) =>
      current.map((r) => (r.key === key ? { ...r, ...patch } : r)),
    );
  const no = quoteLineNo(quote, line);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            Choose quantities · {no} {line.name_full}
          </DialogTitle>
          <DialogDescription>
            Tick the quantities to offer. The price list&apos;s quantities are
            Promo Office&apos;s own price breaks, not prices the team quoted:
            check each price before you offer it. A quantity with no buy price
            needs one: the line keeps the supplier&apos;s cost.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            const refused = await onSave(chosenQuantitiesOf(rows));
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          {/* A list, not a table: below sm each row's source drops under its figures, so nothing scrolls sideways. */}
          <div
            aria-hidden
            className={`${ROW_GRID} text-xs text-muted-foreground max-sm:hidden`}
          >
            <span>Offer</span>
            <span>Quantity</span>
            <span>Price, ex VAT</span>
            <span>Buy price</span>
            <span>Where it comes from</span>
          </div>
          <ul aria-label="Quantities" className="text-sm">
            {rows.map((row) => {
              const rowId = `${id}-${row.key}`;
              const quantityProblem =
                problems[row.key]?.quantity ?? serverProblem(row, "quantity");
              const priceProblem =
                problems[row.key]?.price ?? serverProblem(row, "price_pence");
              const costProblem =
                problems[row.key]?.cost ?? serverProblem(row, "cost_pence");
              const pence = penceOfPounds(row.price);
              const quantity = Number(row.quantity.replace(/,/g, ""));
              const label =
                row.source === "added"
                  ? "the quantity you added"
                  : count(quantity);
              return (
                <li
                  key={row.key}
                  className={`${ROW_GRID} items-start border-t border-border py-2`}
                >
                  <Checkbox
                    id={`${rowId}-offer`}
                    aria-label={`Offer ${label}`}
                    checked={row.isOffered}
                    onCheckedChange={(checked) =>
                      update(row.key, { isOffered: checked === true })
                    }
                    className="mt-2"
                  />
                  <div className="tabular-nums">
                    {row.source === "added" ? (
                      <Input
                        aria-label="Quantity you added"
                        aria-invalid={quantityProblem ? true : undefined}
                        aria-describedby={
                          quantityProblem ? `${rowId}-qty` : undefined
                        }
                        inputMode="numeric"
                        value={row.quantity}
                        onChange={(e) =>
                          update(row.key, { quantity: e.target.value })
                        }
                        className="h-8 tabular-nums"
                      />
                    ) : (
                      <span className="leading-8">{count(quantity)}</span>
                    )}
                    {quantityProblem && (
                      <p id={`${rowId}-qty`} className="text-xs text-red-600">
                        {quantityProblem}
                      </p>
                    )}
                  </div>
                  <div className="tabular-nums">
                    {row.source === "team" ? (
                      <span className="leading-8">
                        {formatQuotedPrice(pence ?? 0)}
                      </span>
                    ) : (
                      <Input
                        aria-label={`Price for ${label}, in pounds`}
                        aria-invalid={priceProblem ? true : undefined}
                        aria-describedby={
                          priceProblem ? `${rowId}-price` : undefined
                        }
                        inputMode="decimal"
                        value={row.price}
                        onChange={(e) =>
                          update(row.key, { price: e.target.value })
                        }
                        className="h-8 tabular-nums"
                      />
                    )}
                    {priceProblem ? (
                      <p id={`${rowId}-price`} className="text-xs text-red-600">
                        {priceProblem}
                      </p>
                    ) : (
                      <p className="whitespace-nowrap text-xs text-muted-foreground">
                        {pence !== null && pence > 0 && quantity > 0
                          ? unitPriceWords(pence, quantity)
                          : "–"}
                      </p>
                    )}
                  </div>
                  <div className="tabular-nums">
                    {needsTypedCost(row) ? (
                      <Input
                        aria-label={`Buy price for ${label}, in pounds`}
                        aria-invalid={costProblem ? true : undefined}
                        aria-describedby={
                          costProblem ? `${rowId}-cost` : undefined
                        }
                        inputMode="decimal"
                        placeholder="Needed"
                        value={row.cost}
                        onChange={(e) =>
                          update(row.key, { cost: e.target.value })
                        }
                        className="h-8 tabular-nums"
                      />
                    ) : (
                      <span className="leading-8 text-muted-foreground">
                        {row.costPence !== null
                          ? formatQuotedPrice(row.costPence)
                          : "The team's"}
                      </span>
                    )}
                    {costProblem && (
                      <p id={`${rowId}-cost`} className="text-xs text-red-600">
                        {costProblem}
                      </p>
                    )}
                  </div>
                  <div className="col-span-3 col-start-2 text-xs text-muted-foreground sm:col-span-1 sm:col-start-auto sm:pt-2">
                    {quantityRowSourceWords(row, priceListLabel)}
                    {row.source === "price_list" &&
                      row.costPence === null &&
                      ". It gives no buy price: enter the supplier's"}
                    {row.source === "added" && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="ml-2"
                        onClick={() =>
                          setRows((current) =>
                            current.filter((r) => r.key !== row.key),
                          )
                        }
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              setRows((current) => [
                ...current,
                {
                  key: `added-${added + 1}`,
                  source: "added",
                  quantity: "",
                  price: "",
                  costPence: null,
                  cost: "",
                  isOffered: true,
                },
              ]);
              setAdded((n) => n + 1);
            }}
          >
            Add a quantity
          </Button>
          <p
            aria-live="polite"
            className="rounded-md bg-muted px-3 py-2 text-sm"
          >
            {Object.keys(problems).length > 0
              ? "Correct the quantities marked above."
              : describeOffer(chosenQuantitiesOf(rows))}
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Back
            </Button>
            <Button type="submit" disabled={busy || hasProblem}>
              Save quantities
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── A possible alternative ─────────────────────────────────────────────────

/** ".01 will offer 500 at £1,050.00 and 1,000 at £1,950.00": the line it repeats, with this one folded in. */
function foldedWords(
  quote: QuoteView,
  repeated: QuoteLineView,
  line: QuoteLineView,
): string {
  const offered =
    repeated.quantity_options.length > 0
      ? repeated.quantity_options.map((o) => [o.quantity, o.price_pence])
      : [[repeated.quantity, repeated.price_pence ?? 0]];
  const all = [...offered, [line.quantity, line.price_pence ?? 0]].sort(
    (a, b) => a[0]! - b[0]!,
  );
  return `${quoteLineNo(quote, repeated)} will offer ${all
    .map(([q, p]) => `${count(q!)} at ${formatQuotedPrice(p!)}`)
    .join(" and ")}, and the customer picks one. This line goes.`;
}

export function ResolveAlternativeDialog({
  quote,
  line,
  busy,
  onClose,
  onSave,
}: {
  quote: QuoteView;
  line: QuoteLineView;
  busy: boolean;
  onClose: () => void;
  onSave: (
    resolution: PossibleAlternativeResolution,
  ) => Promise<QuoteStepFailure | null>;
}) {
  const id = useId();
  const [resolution, setResolution] =
    useState<PossibleAlternativeResolution | null>(null);
  const [failure, setFailure] = useState<QuoteStepFailure | null>(null);
  const repeated = repeatedLineOf(quote, line);
  const no = quoteLineNo(quote, line);
  const repeatedNo = repeated ? quoteLineNo(quote, repeated) : null;
  const choices: Array<{
    value: PossibleAlternativeResolution;
    label: string;
    detail: string;
  }> = [
    ...(repeated
      ? [
          {
            value: "make_quantity_option" as const,
            label: `Make it a quantity option of ${repeatedNo}`,
            detail: foldedWords(quote, repeated, line),
          },
        ]
      : []),
    {
      value: "keep_as_extra_line",
      label: "Keep it as an extra line",
      detail:
        "The customer is quoted for both lines, as Promo Office has them.",
    },
    {
      value: "remove",
      label: "Remove it",
      detail:
        "The line goes from this draft. Promo Office's tender is not changed.",
    },
  ];
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Possible alternative · {no} {line.name_full}
          </DialogTitle>
          <DialogDescription>
            {repeated
              ? `This line repeats the article on ${repeatedNo} ${repeated.name_full}.`
              : "This line repeats an article on another line."}{" "}
            Promo Office has no quantity options, so the team may have quoted a
            second quantity as a line of its own.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!resolution) return;
            const refused = await onSave(resolution);
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <RadioGroup
            aria-label="What to do with this line"
            value={resolution ?? ""}
            onValueChange={(value) =>
              setResolution(value as PossibleAlternativeResolution)
            }
          >
            {choices.map((choice) => (
              <div key={choice.value} className="flex items-start gap-2">
                <RadioGroupItem
                  id={`${id}-${choice.value}`}
                  value={choice.value}
                  aria-describedby={`${id}-${choice.value}-detail`}
                  className="mt-0.5"
                />
                <div>
                  <Label htmlFor={`${id}-${choice.value}`}>
                    {choice.label}
                  </Label>
                  <p
                    id={`${id}-${choice.value}-detail`}
                    className="text-xs text-muted-foreground"
                  >
                    {choice.detail}
                  </p>
                </div>
              </div>
            ))}
          </RadioGroup>
          <p className="border-t border-border pt-3 text-xs text-muted-foreground">
            Does it differ in more than quantity, such as a UK or a Far East
            lead time? Remove it here and quote it on a second draft quote for{" "}
            {quote.customer.name}, linked to{" "}
            {quote.made_from_label ?? "the same tender"}.
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Back
            </Button>
            <Button type="submit" disabled={busy || !resolution}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Change owner ───────────────────────────────────────────────────────────

export function ChangeOwnerDialog({
  quote,
  busy,
  onClose,
  onSave,
}: {
  quote: QuoteView;
  busy: boolean;
  onClose: () => void;
  onSave: (ownerUserId: string) => Promise<QuoteStepFailure | null>;
}) {
  const id = useId();
  const [ownerUserId, setOwnerUserId] = useState(quote.owner_user_id ?? "");
  const [failure, setFailure] = useState<QuoteStepFailure | null>(null);
  const isUnchanged = ownerUserId === (quote.owner_user_id ?? "");
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change owner · {quote.quote_no}</DialogTitle>
          <DialogDescription>
            {quote.made_from_label
              ? "The owner is the merch CSR who will check and publish the draft quote."
              : "The owner is the staff user who will check and publish the draft quote."}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            const refused = await onSave(ownerUserId);
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          {failure && !failure.fieldErrors.owner_user_id && (
            <FailureBanner failure={failure} />
          )}
          <RadioGroup
            aria-label="Owner"
            aria-invalid={failure?.fieldErrors.owner_user_id ? true : undefined}
            value={ownerUserId}
            onValueChange={setOwnerUserId}
          >
            {(quote.owner_choices ?? []).map((choice) => (
              <div key={choice.user_id} className="flex items-center gap-2">
                <RadioGroupItem
                  id={`${id}-${choice.user_id}`}
                  value={choice.user_id}
                />
                <Label htmlFor={`${id}-${choice.user_id}`}>
                  {choice.user_id === quote.owner_user_id
                    ? `${choice.name} (owner now)`
                    : choice.name}
                </Label>
              </div>
            ))}
          </RadioGroup>
          {failure?.fieldErrors.owner_user_id && (
            <p className="text-sm text-red-600">
              {failure.fieldErrors.owner_user_id}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Back
            </Button>
            <Button
              type="submit"
              disabled={busy || !ownerUserId || isUnchanged}
            >
              Change owner
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Choose contact ─────────────────────────────────────────────────────────

export function ChooseContactDialog({
  quote,
  busy,
  onClose,
  onSave,
}: {
  quote: QuoteView;
  busy: boolean;
  onClose: () => void;
  onSave: (contactPersonId: string) => Promise<QuoteStepFailure | null>;
}) {
  const id = useId();
  const choices = quote.contact_choices ?? [];
  const current =
    choices.find((c) => c.email === quote.contact?.email)?.person_id ?? "";
  const [personId, setPersonId] = useState(current);
  const [failure, setFailure] = useState<QuoteStepFailure | null>(null);
  const fieldError = failure?.fieldErrors.contact_person_id;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Choose contact · {quote.quote_no}</DialogTitle>
          <DialogDescription>
            The person at {quote.customer.name} the quote will go to. Staff can
            publish and share the draft quote once the draft quote has a
            contact.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            const refused = await onSave(personId);
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          {failure && !fieldError && <FailureBanner failure={failure} />}
          {choices.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {quote.customer.name} has no people in One yet. Add a person on
              the customer&apos;s page first.
            </p>
          ) : (
            <RadioGroup
              aria-label="Contact"
              aria-invalid={fieldError ? true : undefined}
              value={personId}
              onValueChange={setPersonId}
            >
              {choices.map((choice) => (
                <div key={choice.person_id} className="flex items-start gap-2">
                  <RadioGroupItem
                    id={`${id}-${choice.person_id}`}
                    value={choice.person_id}
                    aria-describedby={`${id}-${choice.person_id}-email`}
                    className="mt-0.5"
                  />
                  <div>
                    <Label htmlFor={`${id}-${choice.person_id}`}>
                      {choice.name}
                    </Label>
                    <p
                      id={`${id}-${choice.person_id}-email`}
                      className="text-xs text-muted-foreground"
                    >
                      {choice.email}
                    </p>
                  </div>
                </div>
              ))}
            </RadioGroup>
          )}
          {fieldError && <p className="text-sm text-red-600">{fieldError}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Back
            </Button>
            <Button
              type="submit"
              disabled={busy || !personId || personId === current}
            >
              Choose contact
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Choose delivery address ────────────────────────────────────────────────

export function ChooseDeliveryAddressDialog({
  quote,
  busy,
  addAddressHref,
  onClose,
  onSave,
}: {
  quote: QuoteView;
  busy: boolean;
  /** The customer's page, where a new address is added; null when the customer has no staff page. */
  addAddressHref: string | null;
  onClose: () => void;
  onSave: (addressId: string) => Promise<QuoteStepFailure | null>;
}) {
  const id = useId();
  const choices = quote.delivery_address_choices ?? [];
  const total = quote.delivery_address_choice_total ?? choices.length;
  const current = quote.delivery_address_id ?? "";
  const [addressId, setAddressId] = useState(current);
  const [failure, setFailure] = useState<QuoteStepFailure | null>(null);
  const fieldError = failure?.fieldErrors.address_id;
  const addAddress = addAddressHref ? (
    <Link to={addAddressHref} className="underline">
      add one on {quote.customer.name}&apos;s page
    </Link>
  ) : null;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Choose delivery address · {quote.quote_no}</DialogTitle>
          <DialogDescription>
            Where every item on the draft quote will go. The delivery price will
            not be recalculated: the draft quote&apos;s price stands.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            const refused = await onSave(addressId);
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          {failure && !fieldError && <FailureBanner failure={failure} />}
          {choices.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {quote.customer.name} has no delivery addresses in One yet
              {addAddress ? <>: {addAddress}.</> : "."}
            </p>
          ) : (
            <RadioGroup
              aria-label="Delivery address"
              aria-invalid={fieldError ? true : undefined}
              value={addressId}
              onValueChange={setAddressId}
              className="max-h-80 overflow-y-auto"
            >
              {choices.map((choice) => (
                <div key={choice.address_id} className="flex items-start gap-2">
                  <RadioGroupItem
                    id={`${id}-${choice.address_id}`}
                    value={choice.address_id}
                    aria-describedby={`${id}-${choice.address_id}-lines`}
                    className="mt-0.5"
                  />
                  <div>
                    <Label htmlFor={`${id}-${choice.address_id}`}>
                      {choice.name}
                      {choice.is_default && (
                        <>
                          {" "}
                          <span className="font-normal text-muted-foreground">
                            (default)
                          </span>
                        </>
                      )}
                    </Label>
                    <p
                      id={`${id}-${choice.address_id}-lines`}
                      className="text-xs text-muted-foreground"
                    >
                      {choice.lines.join(", ")}
                    </p>
                  </div>
                </div>
              ))}
            </RadioGroup>
          )}
          {choices.length > 0 && (total > choices.length || addAddress) && (
            <p className="text-xs text-muted-foreground">
              {total > choices.length &&
                `Showing ${count(choices.length)} of ${count(total)} addresses. `}
              {addAddress && <>Not listed? You can {addAddress}.</>}
            </p>
          )}
          {fieldError && <p className="text-sm text-red-600">{fieldError}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Back
            </Button>
            <Button
              type="submit"
              disabled={busy || !addressId || addressId === current}
            >
              Deliver here
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
